import { Head, Link } from "@inertiajs/react"
import {
  ArrowLeft,
  Coffee as CoffeeIcon,
  Droplets,
  Scale,
  Thermometer,
  Timer,
  Wheat,
  Lightbulb,
} from "lucide-react"

import type { PublishedCoffeeRecipe } from "@/types"

interface CoffeeData {
  name: string
  slug: string
}

interface Organization {
  name: string
  slug: string
  logo_url: string | null
  primary_color: string | null
  secondary_color: string | null
  website_url: string | null
  instagram_handle: string | null
}

interface Props {
  coffee: CoffeeData
  recipe: PublishedCoffeeRecipe
  organization: Organization | null
}

const methodLabels: Record<string, string> = {
  espresso: "Espresso",
  french_press: "French Press",
  auto_drip: "Auto Drip",
  v60: "V60 / Conic Pour Over",
  kalita_wave: "Kalita Wave / Flat Pour Over",
  elf_dripper: "Elf Dripper",
  origami: "Origami",
  eggonaut: "Eggonaut",
  meteorite_dripper: "Meteorite Dripper",
}

const categoryLabels: Record<string, string> = {
  espresso: "Espresso",
  immersion: "Immersion",
  auto_drip: "Auto Drip",
  conic_pour_over: "Conic Pour Over",
  flat_pour_over: "Flat Pour Over",
}

export default function PublicRecipe({ coffee, recipe, organization }: Props) {
  const primaryColor = organization?.primary_color || "#6F4E37"
  const methodLabel =
    methodLabels[recipe.method] || recipe.method.replace(/_/g, " ")

  const stats: {
    label: string
    value: string | null
    icon: typeof Scale
  }[] = [
    {
      label: "Dose",
      value: recipe.dose ? `${recipe.dose} g` : null,
      icon: Scale,
    },
    {
      label: "Yield",
      value: recipe.yield_amount ? `${recipe.yield_amount} ml` : null,
      icon: Droplets,
    },
    { label: "Ratio", value: recipe.ratio, icon: Scale },
    {
      label: "Temperature",
      value: recipe.temp ? `${recipe.temp}°C` : null,
      icon: Thermometer,
    },
    { label: "Time", value: recipe.time, icon: Timer },
    { label: "Grind", value: recipe.grind, icon: Wheat },
  ]

  return (
    <>
      <Head title={`${methodLabel} — ${coffee.name}`} />

      <div className="min-h-screen bg-white dark:bg-neutral-950">
        <header className="border-b px-4 py-4 sm:px-6">
          <div className="mx-auto flex max-w-3xl items-center justify-between">
            <Link
              href={`/c/${coffee.slug}`}
              className="inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>{coffee.name}</span>
            </Link>
            {organization?.logo_url ? (
              <img
                src={organization.logo_url}
                alt={organization.name}
                className="h-8 w-8 rounded-full object-cover"
              />
            ) : (
              <div
                className="flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold text-white"
                style={{ backgroundColor: primaryColor }}
              >
                {organization?.name?.charAt(0) || "C"}
              </div>
            )}
          </div>
        </header>

        <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
          <div className="mb-8 flex items-center gap-4">
            <div
              className="flex h-14 w-14 items-center justify-center rounded-full"
              style={{ backgroundColor: `${primaryColor}1A` }}
            >
              <CoffeeIcon className="h-7 w-7" style={{ color: primaryColor }} />
            </div>
            <div>
              {recipe.category && (
                <p className="text-xs font-medium tracking-wide text-neutral-500 uppercase dark:text-neutral-400">
                  {categoryLabels[recipe.category] || recipe.category}
                </p>
              )}
              <h1 className="text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl dark:text-neutral-100">
                {methodLabel}
              </h1>
            </div>
          </div>

          <section className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {stats
              .filter((s) => s.value)
              .map(({ label, value, icon: Icon }) => (
                <div
                  key={label}
                  className="rounded-lg border bg-neutral-50 p-4 dark:bg-neutral-900"
                >
                  <div className="mb-2 flex items-center gap-2 text-neutral-500">
                    <Icon className="h-4 w-4" />
                    <span className="text-xs font-medium tracking-wide uppercase">
                      {label}
                    </span>
                  </div>
                  <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                    {value}
                  </p>
                </div>
              ))}
          </section>

          {recipe.ai_tip && (
            <section
              className="rounded-lg border-l-4 bg-neutral-50 p-5 dark:bg-neutral-900"
              style={{ borderLeftColor: primaryColor }}
            >
              <div className="mb-2 flex items-center gap-2">
                <Lightbulb
                  className="h-4 w-4"
                  style={{ color: primaryColor }}
                />
                <h2 className="text-sm font-semibold tracking-wide text-neutral-700 uppercase dark:text-neutral-300">
                  Brewing Tip
                </h2>
              </div>
              <p className="leading-relaxed text-neutral-700 dark:text-neutral-300">
                {recipe.ai_tip}
              </p>
            </section>
          )}

          <div className="mt-10">
            <Link
              href={`/c/${coffee.slug}`}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to {coffee.name}
            </Link>
          </div>
        </main>

        <footer className="border-t px-4 py-6 text-center sm:px-6">
          <p className="text-xs text-neutral-400">
            Powered by{" "}
            <a
              href="https://catador.pro"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
            >
              Catador Pro
            </a>
          </p>
        </footer>
      </div>
    </>
  )
}
